/**
 * What a capture says of where its photo came from (ADR-0059). A photo from the camera input
 * was taken now. One from a file — the phone's gallery, the desktop's file dialog, a file
 * dropped on the page — was taken earlier: its capture is *imported* and carries the time the
 * photo itself names, when it names one. The capture's own time stays the time it was added.
 */
import { photoSource } from '@/components/humint/check/photo-source'
import type { PhotoOrigin } from '@/components/humint/types'
import { readPhotoTime } from './photo-time'

export type { PhotoOrigin }

export const TAKEN_NOW: PhotoOrigin = { imported: false, takenAt: null }

/**
 * A photo this fresh was taken now, whichever way it came: a phone's file dialog also offers
 * its camera.
 */
export const FRESH_MS = 2 * 60_000
/** A phone's clock this far ahead is believed; the server draws the same line. */
const SKEW_MS = 5 * 60_000

/** The origin of a photo from a file, given the time it names. */
export function importedOrigin(taken: Date | null, now: Date): PhotoOrigin {
  if (!taken || taken.getTime() > now.getTime() + SKEW_MS) return { imported: true, takenAt: null }
  if (now.getTime() - taken.getTime() < FRESH_MS) return TAKEN_NOW
  return { imported: true, takenAt: taken.toISOString() }
}

/** The origin of a picked photo. Read from the picked file itself, before it is re-encoded. */
export async function photoOrigin(file: Blob, now: Date = new Date()): Promise<PhotoOrigin> {
  if (photoSource(file) === 'camera') return TAKEN_NOW
  return importedOrigin(await readPhotoTime(file), now)
}

/**
 * The origin as a capture shown on a page carries it: nothing for a photo taken in the
 * workspace, and a time only with *imported*.
 */
export const importedFields = (
  imported: boolean | null | undefined,
  takenAt: string | null | undefined
): Partial<PhotoOrigin> => (imported ? { imported: true, takenAt: takenAt ?? null } : {})
